import type { ResumeDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export const uploadResume = async (
  file: File,
  onProgress?: (percent: number) => void,
) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post<ResumeDto>("/resume/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event) => {
      if (event.total && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

  return response.data;
};

type UploadParams = {
  file: File;
  onProgress?: (percent: number) => void;
};

export const useUploadResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: uploadResumeFn,
  } = useMutation({
    mutationFn: ({ file, onProgress }: UploadParams) => uploadResume(file, onProgress),
    onSuccess: (data) => {
      queryClient.setQueryData<ResumeDto>(["resume", { id: data.id }], data);

      queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
        if (!cache) return [data];
        return [...cache, data];
      });
    },
  });

  return { uploadResume: uploadResumeFn, loading, error };
};
